import type { DataGroup } from '@/widgets/TreeDataGrid';
import type { DimensionFilter, MetricFilter } from '@/features/filters';
import type { ColumnsConfig } from '@/entities/columnsConfig';
import { baseApi, getErrorMessage } from '@/shared/api';
import { convertUTCtoISO } from '@/shared/lib';
import type { DateScale, FilterDateRangeDTO, Sorting } from '@/shared/types';
import type {
  CohortAgeScale,
  ColumnsMeta,
  DataUpdateInfo,
  TreeDataResponse,
} from '../model/types';
import type {
  DataResponse,
  DataUpdateInfoDTO,
  ColumnsMetaDTO,
  GetDataParams,
  GetStringFilterParams,
  GetNumberFilterParams,
} from './types';

export const getDataUpdateInfo = async (): Promise<DataUpdateInfo> => {
  const { data, error } = await baseApi.functions.invoke<DataUpdateInfoDTO>(
    'getCalendarInsightsDataUpdateInfo',
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return {
    updateTime: convertUTCtoISO(data.updateTime),
  };
};

export const getColumnsMeta = async (): Promise<ColumnsMeta> => {
  const { data, error } = await baseApi.functions.invoke<ColumnsMetaDTO>(
    'getCohortRetentionColumns',
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return {
    defaultColumns: data.defaultColumns,
    config: Object.fromEntries(
      Object.entries(data.config).map(([key, value]) => {
        return [
          key,
          {
            group: value.ui_column_group,
            name: value.ui_column_name,
            description: value.ui_column_description,
            type: value.ui_column_format,
            order: value.ui_cohort_retention_drawer_filter_order,
            dataType: value.ui_column_data_type,
            isAdvancedSortable: value.ui_is_advanced_sorting_available,
          },
        ];
      }),
    ) as ColumnsConfig,
    dimensions: data.dimensions,
    metrics: data.metrics,
  };
};

interface GetTotalParams {
  dimensions: string[];
  metric: string;
  dateScale: DateScale;
  dateRange: FilterDateRangeDTO;
  cohortAgeScale: CohortAgeScale;
  dimensionsFilters: DimensionFilter[];
  isForecasted: boolean;
  probabilisticAttribution: boolean;
}

export const getTotal = async (
  params: GetTotalParams,
): Promise<Record<string, number>> => {
  const { data, error } = await baseApi.functions.invoke<
    Record<string, number>
  >('getCohortRetentionTotal', {
    body: params,
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getData = async ({
  isForecasted,
  dimensions,
  metric,
  dateScale,
  dateRange,
  cohortAgeScale,
  offset,
  sort,
  probabilisticAttribution,
  dimensionsFilters,
  metricsFilters,
  pageSize,
}: GetDataParams) => {
  const { data, error } = await baseApi.functions.invoke<DataResponse>(
    'getCohortRetentionRows',
    {
      body: {
        isForecasted,
        dimensions,
        metric,
        dateScale,
        dateRange,
        cohortAgeScale,
        dimensionsFilters,
        metricsFilters,
        probabilisticAttribution,
        sort,
        offset,
        pageSize,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};

export const getStringFilterParams = async (params: GetStringFilterParams) => {
  const filtersWithoutCurrent = params.dimensionsFilters.filter((filter) => {
    return filter.id !== params.filterName;
  });

  const { data, error } = await baseApi.functions.invoke<{
    options: string[];
  }>('getCohortRetentionStringFilterParams', {
    body: {
      filterName: params.filterName,
      dimensionsFilters: filtersWithoutCurrent,
      dateRange: params.dateRange,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

export const getNumberFilterParams = async (params: GetNumberFilterParams) => {
  const { data, error } = await baseApi.functions.invoke<{
    min: number;
    max: number;
  }>('getCohortRetentionNumberFilterParams', {
    body: {
      filterName: params.filterName,
      dimensions: params.dimensions,
      dimensionsFilters: params.dimensionsFilters,
      metricsFilters: params.metricsFilters,
      dateRange: params.dateRange,
      dateScale: params.dateScale,
      cohortAgeScale: params.cohortAgeScale,
      isForecasted: params.isForecasted,
      probabilisticAttribution: params.probabilisticAttribution,
      groupedData: params.groupedData,
    },
  });

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data;
};

interface GetTreeDataParams {
  dimensions: [string];
  metric: string;
  dateScale: DateScale;
  dateRange: FilterDateRangeDTO;
  sort: Sorting;
  cohortAgeScale: CohortAgeScale;
  probabilisticAttribution: boolean;
  isForecasted: boolean;
  dimensionsFilters: DimensionFilter[];
  metricsFilters: MetricFilter[];
  parentGroups: DataGroup[];
}

export const getTreeData = async ({
  dimensions,
  metric,
  dateScale,
  dateRange,
  sort,
  probabilisticAttribution,
  cohortAgeScale,
  isForecasted,
  dimensionsFilters,
  metricsFilters,
  parentGroups,
}: GetTreeDataParams) => {
  const { data, error } = await baseApi.functions.invoke<TreeDataResponse>(
    'getCohortRetentionTreeViewData',
    {
      body: {
        dimensions,
        metric,
        dateScale,
        dateRange,
        sort,
        probabilisticAttribution,
        cohortAgeScale,
        isForecasted,
        dimensionsFilters,
        metricsFilters,
        parentGroups,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};
